import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { makeRequestAuth } from "../../../_config/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countries } from "./CountryName";
import {
  PlaneTakeoff, PlaneLanding, Globe,
  MapPin, CreditCard, Plus, Trash2,
  Calendar, Clock, ShieldCheck, AlertCircle,
  History, ArrowRight
} from "lucide-react";

/* ------------------ CONSTANTS ------------------ */
const EMPTY_TRAVEL = () => ({
  country: "",
  visaType: "",
  stayAddress: "",
  from: "",
  to: "",
});

const TravelForm = () => {
  const [travels, setTravels] = useState([EMPTY_TRAVEL()]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  /* ------------------ HELPERS ------------------ */
  const normalizeDate = (date) => (date ? date.split("T")[0] : "");

  useEffect(() => {

    const fetchTravelHistory = async () => {
      setLoading(true);
      try {
        const res = await makeRequestAuth("get", "/user/getTravelHistory");
        if (res?.data?.travels?.length) {
          const normalized = res.data.travels.map((t) => ({
            ...t,
            from: normalizeDate(t.from),
            to: normalizeDate(t.to),
          }));
          setTravels(normalized);
          setPreview({ ...res.data, travels: normalized });
        }
      } catch (err) {
        console.log("No existing history found.");
      } finally {
        setLoading(false);
      }
    };

    fetchTravelHistory();
  }, []);





  const handleChange = (index, field, value) => {
    const updated = [...travels];
    updated[index] = { ...updated[index], [field]: value };

    if (field === "from" && updated[index].to && updated[index].to < value) {
      updated[index].to = "";
    }
    setTravels(updated);
  };

  const addTravel = () => {
    setTravels([...travels, EMPTY_TRAVEL()]);
  };

  const removeTravel = (index) => {
    if (travels.length === 1) {
      setTravels([EMPTY_TRAVEL()]);
    } else {
      setTravels(travels.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    const errs = [];
    travels.forEach((t, i) => {
      const row = `Journey ${i + 1}`;
      if (!t.country) errs.push(`${row}: Destination Country is required`);
      if (!t.visaType?.trim()) errs.push(`${row}: Visa Type is required`);
      if (!t.from) errs.push(`${row}: Departure Date is required`);
      if (!t.to) errs.push(`${row}: Return Date is required`);
      if (t.from && t.to && new Date(t.to) < new Date(t.from)) {
        errs.push(`${row}: Return date cannot be earlier than departure`);
      }
    });
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    const validationErrors = validateForm();
    if (validationErrors.length) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setLoading(true);
    try {
      const cleanedTravels = travels.map(({ _id, ...rest }) => rest);
      const res = await makeRequestAuth("post", "/user/createOrUpdateTravelHistory", { travels: cleanedTravels });
      setPreview(res?.data);
      showSuccessToast("Travel history saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending" },
    approve: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <ShieldCheck size={14} />, label: "Approve" },
    reject: { bg: "bg-rose-50 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Rejected" },
  };


  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 antialiased font-normal">
      <ToastContainer />

      {/* HEADER */}
      <div className="bg-[#132b45] rounded-t-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl">
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-500/20 rounded-2xl border border-indigo-400/30">
              <PlaneTakeoff size={32} className="text-indigo-300" />
            </div>
            <div>
              <h1 className="text-2xl font-medium tracking-tight">Travel Declaration</h1>
              <p className="text-indigo-100/60 text-xs mt-1 font-normal uppercase tracking-wider">Historical International Movements</p>
            </div>
          </div>
          {preview && (
            <div className={`flex items-center gap-2 px-5 py-2 rounded-full border text-[10px] font-normal uppercase tracking-widest shadow-inner ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.bg || "bg-slate-100 text-slate-600"}`}>
              {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon}
              {STATUS_CONFIG[preview?.status?.toLowerCase()]?.label || "Submitted"}
            </div>
          )}
        </div>
        <Globe className="absolute -right-12 -bottom-12 w-48 h-48 text-white/5 rotate-12" />
      </div>

      <div className="bg-white rounded-b-[2.5rem] shadow-xl border-x border-b border-gray-100 p-8">
        {/* ERRORS */}
        {errors.length > 0 && (
          <div className="mb-8 bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-2xl animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-2 text-rose-800 font-medium mb-2 uppercase text-[10px] tracking-widest">
              <AlertCircle size={16} /> Error Details
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-xs text-rose-600 font-normal">
              {errors.map((e, i) => <li key={i} className="py-0.5">• {e}</li>)}
            </ul>
          </div>
        )}

        {preview && !loading ? (
          /* PREVIEW MODE */
          <div className="space-y-6 animate-in fade-in duration-500">
            <div className="grid gap-4">
              {preview.travels.map((data, i) => (
                <div key={i} className="bg-slate-50/50 border border-slate-100 rounded-[2rem] p-6 group hover:bg-white transition-all hover:shadow-lg">
                  <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6 border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center font-medium">{i + 1}</div>
                      <span className="font-medium text-[#132b45] text-base">{data.country}</span>
                    </div>
                    <div className="flex items-center gap-2 text-indigo-900/60 font-normal text-[10px] bg-white px-4 py-2 rounded-full border border-slate-100">
                      <Calendar size={12} /> {formatDate(data.from)} <ArrowRight size={10} /> {formatDate(data.to)}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <PreviewItem label="Visa Type" value={data.visaType} icon={<CreditCard size={14} />} />
                    <PreviewItem label="Stay Address" value={data.stayAddress} icon={<MapPin size={14} />} />
                  </div>
                </div>
              ))}
            </div>
            {(preview.status === "reject" || !preview.status) && (
              <div className="flex justify-center pt-6">
                <button onClick={() => setPreview(null)} className="flex items-center gap-3 bg-[#132b45] text-white px-12 py-4 rounded-2xl font-normal text-sm hover:bg-black transition-all active:scale-95 shadow-2xl shadow-blue-900/20">
                  <History size={18} /> Modify Travel
                </button>
              </div>
            )}
          </div>
        ) : (
          /* FORM MODE */
          <form onSubmit={handleSubmit} className="space-y-10">
            {travels.map((travel, index) => (
              <div key={index} className="bg-white rounded-[2.5rem] border-2 border-slate-50 p-8 relative group hover:border-indigo-100 transition-all shadow-sm">
                <div className="flex justify-between items-center mb-10">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-2xl bg-[#132b45] text-white flex items-center justify-center font-normal text-sm shadow-lg shadow-blue-900/20">{index + 1}</span>
                    <h3 className="font-normal text-[#132b45] uppercase tracking-widest text-[10px]">Trip Data</h3>
                  </div>
                  {travels.length > 1 && (
                    <button type="button" onClick={() => removeTravel(index)} className="text-rose-400 hover:text-rose-600 p-2 hover:bg-rose-50 rounded-xl transition-all">
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <FormLabel label="Destination Country" icon={<Globe size={14} />} />
                    <select
                      value={travel.country}
                      onChange={(e) => handleChange(index, "country", e.target.value)}
                      className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-4 focus:border-indigo-500 focus:bg-white outline-none transition-all font-normal text-[#132b45] text-sm appearance-none cursor-pointer"
                    >
                      <option value="">Select Country</option>
                      {countries.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <Input label="Visa Type" icon={<CreditCard size={14} />} placeholder="e.g. Tourist, Business" value={travel.visaType} onChange={(v) => handleChange(index, "visaType", v)} />

                  <div className="md:col-span-2">
                    <Input label="Accommodation Address" icon={<MapPin size={14} />} placeholder="Where did you stay?" value={travel.stayAddress} onChange={(v) => handleChange(index, "stayAddress", v)} />
                  </div>

                  <Input
                    type="date"
                    label="Departure Date"
                    icon={<PlaneTakeoff size={14} />}
                    max={new Date().toISOString().split("T")[0]}
                    value={travel.from}
                    onChange={(v) => handleChange(index, "from", v)}
                  />
                  <Input
                    type="date"
                    label="Return Date"
                    icon={<PlaneLanding size={14} />}
                    min={travel.from}
                    value={travel.to}
                    onChange={(v) => handleChange(index, "to", v)}
                  />
                </div>
              </div>
            ))}

            <div className="flex flex-col md:flex-row gap-4 pt-6">
              <button
                type="button"
                onClick={addTravel}
                className="flex-1 flex items-center justify-center gap-2 bg-slate-50 border-2 border-dashed border-slate-200 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 py-5 rounded-[2rem] font-normal text-[10px] uppercase tracking-wider transition-all"
              >
                <Plus size={20} /> Add Another Trip
              </button>

              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#132b45] hover:bg-black text-white py-5 rounded-[2rem] font-normal text-[10px] uppercase tracking-wider shadow-2xl shadow-blue-900/20 transition-all flex items-center justify-center gap-3"
              >
                {loading ? <Clock className="animate-spin" /> : "Save Travel"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* LOADER */}
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-md z-50 flex items-center justify-center">
          <div className="w-14 h-14 border-4 border-slate-100 border-t-[#132b45] rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

/* --- UI HELPERS --- */
const FormLabel = ({ label, icon }) => (
  <label className="text-[10px] font-normal text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2 mb-1">
    {icon} {label}
  </label>
);

const Input = ({ label, value, onChange, type = "text", icon, placeholder, min, max }) => (
  <div className="space-y-1">
    <FormLabel label={label} icon={icon} />
    <input
      type={type}
      value={value || ""}
      min={min}
      max={max}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-4 focus:bg-white focus:border-indigo-500 outline-none transition-all font-normal text-[#132b45] text-sm"
    />
  </div>
);

const PreviewItem = ({ label, value, icon }) => (
  <div className="flex gap-3">
    <div className="mt-1 h-8 w-8 bg-white border border-slate-100 rounded-lg flex items-center justify-center text-indigo-400 shrink-0">{icon}</div>
    <div>
      <span className="text-[10px] font-normal text-slate-400 uppercase tracking-widest block mb-0.5">{label}</span>
      <p className="font-normal text-[#132b45] text-sm leading-tight">{value || "—"}</p>
    </div>
  </div>
);

export default TravelForm;